import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Upload, Star, X, Loader2, ImageIcon, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL || ""}${u}`;
};

/**
 * GalleryUploader — gestione galleria multi-foto per il prodotto madre.
 *
 *  - La PRIMA foto della lista è la COVER (mostrata in vetrina come anteprima
 *    principale).
 *  - L'utente può: caricare N foto in batch, riordinare con ← →, rendere
 *    cover (Star), eliminare.
 *  - Visivamente: una "cover" grande a sx + griglia di thumbnails a dx.
 *
 * Props:
 *   - value: string[] (URLs delle immagini)
 *   - onChange(urls: string[])
 *   - testid?: string
 *   - max?: number (default 12)
 */
export default function GalleryUploader({ value = [], onChange, testid = "gallery", max = 12 }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const list = Array.isArray(value) ? value : [];

  const uploadBatch = async (files) => {
    if (!files?.length) return;
    if (list.length + files.length > max) {
      toast.error(`Massimo ${max} foto. Hai già ${list.length}.`);
      return;
    }
    setBusy(true);
    const newUrls = [];
    let errors = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data?.url) newUrls.push(data.url);
      } catch (e) {
        errors++;
      }
    }
    setBusy(false);
    if (newUrls.length) {
      onChange?.([...list, ...newUrls]);
      toast.success(`${newUrls.length} foto caricate${errors ? ` (${errors} errori)` : ""}`);
    } else if (errors) {
      toast.error("Nessuna foto caricata");
    }
  };

  const removeAt = (i) => {
    const next = list.filter((_, idx) => idx !== i);
    onChange?.(next);
  };

  const moveTo = (i, dir) => {
    const next = [...list];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange?.(next);
  };

  const setCover = (i) => {
    if (i === 0) return;
    const next = [...list];
    const [item] = next.splice(i, 1);
    next.unshift(item);
    onChange?.(next);
    toast.success("Cover aggiornata");
  };

  return (
    <div data-testid={testid} className="space-y-2">
      {/* Cover */}
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Cover (prima foto)</div>
          <div className="aspect-square bg-neutral-100 rounded-sm overflow-hidden border border-border relative group">
            {list[0] ? (
              <img src={resolveUrl(list[0])} alt="cover" className="w-full h-full object-cover" data-testid={`${testid}-cover`} />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground/60">
                <ImageIcon className="w-10 h-10 mb-2" strokeWidth={1.2} />
                <span className="text-xs">Nessuna foto</span>
              </div>
            )}
            {list[0] && (
              <button onClick={() => removeAt(0)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-foreground/80 text-white opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center"
                title="Elimina cover" data-testid={`${testid}-cover-remove`}>
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="col-span-5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center justify-between">
            <span>Galleria ({list.length}/{max})</span>
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}
          </div>
          <div className="grid grid-cols-3 gap-1.5 max-h-72 overflow-auto pr-1">
            {list.slice(1).map((u, idx) => {
              const i = idx + 1;
              return (
                <div key={`${u}-${i}`} className="relative aspect-square bg-neutral-100 rounded-sm overflow-hidden border border-border group" data-testid={`${testid}-thumb-${i}`}>
                  <img src={resolveUrl(u)} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                    <div className="flex gap-1">
                      <button onClick={() => moveTo(i, -1)} disabled={i <= 1}
                        className="w-6 h-6 rounded-sm bg-white/95 text-foreground disabled:opacity-40 inline-flex items-center justify-center"
                        title="Sposta a sinistra" data-testid={`${testid}-left-${i}`}>
                        <ArrowLeft className="w-3 h-3" />
                      </button>
                      <button onClick={() => moveTo(i, 1)} disabled={i >= list.length - 1}
                        className="w-6 h-6 rounded-sm bg-white/95 text-foreground disabled:opacity-40 inline-flex items-center justify-center"
                        title="Sposta a destra" data-testid={`${testid}-right-${i}`}>
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setCover(i)}
                        className="w-6 h-6 rounded-sm bg-amber-400 text-foreground inline-flex items-center justify-center"
                        title="Imposta come cover" data-testid={`${testid}-cover-set-${i}`}>
                        <Star className="w-3 h-3" />
                      </button>
                      <button onClick={() => removeAt(i)}
                        className="w-6 h-6 rounded-sm bg-red-500 text-white inline-flex items-center justify-center"
                        title="Elimina" data-testid={`${testid}-remove-${i}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            {list.length < max && (
              <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                className="aspect-square border border-dashed border-border rounded-sm text-muted-foreground hover:text-foreground hover:border-foreground inline-flex flex-col items-center justify-center text-[10px] disabled:opacity-50"
                data-testid={`${testid}-add-btn`}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mb-0.5" />}
                <span>Aggiungi</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <input ref={fileRef} type="file" accept="image/*" multiple onChange={(e) => { uploadBatch(Array.from(e.target.files || [])); e.target.value = ""; }} className="hidden" data-testid={`${testid}-file-input`} />

      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
        <span>💡 La prima foto è la cover. Click su <Star className="inline w-3 h-3 -mt-0.5 text-amber-500" /> per cambiarla.</span>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy || list.length >= max}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-border hover:bg-accent disabled:opacity-50" data-testid={`${testid}-bulk-btn`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          Carica foto (multi-selezione)
        </button>
      </div>
    </div>
  );
}
